const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
    background: var(--ctl-color-overlay-8);
    backdrop-filter: blur(var(--ctl-space-0-5));
    align-items: center;
    justify-content: center;
  }

  :host([open]) {
    display: flex;
  }

  .overlay-content {
    background: var(--ctl-color-surface, #fff);
    border-radius: var(--ctl-radius-xl, var(--ctl-radius-lg));
    padding: var(--ctl-space-6);
    min-width: 280px;
    max-width: 400px;
    text-align: center;
    box-shadow: var(--ctl-shadow-xl);
  }

  .spinner {
    width: 48px;
    height: 48px;
    border: var(--ctl-space-1) solid var(--ctl-color-overlay-5);
    border-top-color: var(--ctl-color-primary, #2563eb);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto var(--ctl-space-5);
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .label {
    font: var(--ctl-font-weight-semibold) var(--ctl-font-size-base) var(--ctl-font-sans, system-ui);
    color: var(--ctl-color-text);
    margin-bottom: var(--ctl-space-3);
  }

  .label:empty {
    display: none;
  }

  .progress-bar-container {
    width: 100%;
    height: var(--ctl-space-2);
    background: var(--ctl-color-overlay-5);
    border-radius: var(--ctl-radius-sm);
    overflow: hidden;
    margin-bottom: var(--ctl-space-4);
  }

  .progress-bar {
    height: 100%;
    background: var(--ctl-color-primary, #2563eb);
    border-radius: var(--ctl-radius-sm);
    transition: width 0.3s ease;
    width: 0%;
  }

  :host(:not([progress])) .progress-bar-container {
    display: none;
  }

  .custom-content {
    color: var(--ctl-color-muted);
    font: var(--ctl-font-weight-normal) var(--ctl-font-size-base) var(--ctl-font-sans, system-ui);
    line-height: 1.5;
  }

  .custom-content:empty {
    display: none;
  }
</style>

<div class="overlay-content" part="content">
  <div class="spinner" part="spinner"></div>
  <div class="label" part="label"></div>
  <div class="progress-bar-container" part="progress-container">
    <div class="progress-bar" part="progress-bar"></div>
  </div>
  <div class="custom-content" part="custom">
    <slot></slot>
  </div>
</div>
`;

export class CtlProgressOverlay extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
  }

  static get observedAttributes() {
    return ["open", "label", "progress"];
  }

  connectedCallback() {
    // Update label and progress
    this._updateLabel();
    this._updateProgress();

    // Handle ESC key to close (optional - might want to prevent closing during critical operations)
    this._handleKeydown = (e) => {
      if (e.key === "Escape" && this.hasAttribute("open")) {
        this.close("escape");
      }
    };

    // Trap focus when open
    this._handleFocusTrap = (e) => {
      if (!this.hasAttribute("open")) return;
      
      const path = typeof e.composedPath === "function" ? e.composedPath() : null;
      const isInside =
        (path && (path.includes(this) || (this.shadowRoot && path.includes(this.shadowRoot)))) ||
        this === e.target ||
        this.contains(e.target) ||
        (this.shadowRoot && this.shadowRoot.contains(e.target));

      // If focus leaves the overlay, bring it back
      if (!isInside) {
        e.preventDefault();
        this.focus();
      }
    };
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this._handleKeydown);
    document.removeEventListener("focusin", this._handleFocusTrap);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "open") {
      if (newValue !== null && oldValue === null) {
        this._onOpen();
      } else if (newValue === null && oldValue !== null) {
        this._onClose();
      }
    } else if (name === "label") {
      this._updateLabel();
    } else if (name === "progress") {
      this._updateProgress();
    }
  }

  _onOpen() {
    // Add event listeners
    document.addEventListener("keydown", this._handleKeydown);
    document.addEventListener("focusin", this._handleFocusTrap);
    
    // Set tabindex so it can receive focus
    this.setAttribute("tabindex", "-1");
    this.focus();

    this._dispatchEvent("ctl-open");
  }

  _onClose() {
    // Remove event listeners
    document.removeEventListener("keydown", this._handleKeydown);
    document.removeEventListener("focusin", this._handleFocusTrap);
    
    this._dispatchEvent("ctl-close");
  }

  _updateLabel() {
    const label = this.getAttribute("label");
    const labelElement = this.shadowRoot.querySelector(".label");
    if (labelElement) {
      labelElement.textContent = label || "";
    }
  }

  _updateProgress() {
    const progress = parseFloat(this.getAttribute("progress"));
    const progressBar = this.shadowRoot.querySelector(".progress-bar");
    if (progressBar && !isNaN(progress)) {
      const clampedProgress = Math.max(0, Math.min(100, progress));
      progressBar.style.width = clampedProgress + "%";
    }
  }

  open(reason = "programmatic") {
    if (this.hasAttribute("open")) return;
    this.setAttribute("open", "");
  }

  close(reason = "programmatic") {
    if (!this.hasAttribute("open")) return;
    this.removeAttribute("open");
  }

  setProgress(value) {
    this.setAttribute("progress", String(value));
  }

  _dispatchEvent(name, detail = {}) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        composed: true,
        detail
      })
    );
  }
}

customElements.define("ctl-progress-overlay", CtlProgressOverlay);
